'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["variant", "slots", "slotProps", "className", "classes", "sx"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ConversationListRoot, ConversationListItemAvatar, ConversationListItemContent, ConversationListTitle, ConversationListPreview, ConversationListTimestamp, ConversationListUnreadBadge, ConversationListItemActions } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatConversationListUtilityClasses } from "./chatConversationListClasses.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatConversationList');
const ChatConversationListStyled = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root
})(({
  theme
}) => ({
  '--ChatBox-conversationListWidth': '260px',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(0.5, 0),
  listStyle: 'none',
  margin: 0,
  width: '100%',
  boxSizing: 'border-box'
}));
const ChatConversationListScrollerStyled = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'Scroller',
  overridesResolver: (_, styles) => styles.scroller
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid',
  borderRightColor: (theme.vars || theme).palette.divider,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  width: 'var(--ChatBox-conversationListWidth, 260px)',
  height: '100%',
  overflow: 'hidden',
  flexShrink: 0,
  '@container (max-width: 599.95px)': {
    display: 'none'
  }
}));

// Plain-div viewport: the scroller above is a regular div (not ScrollArea.Root),
// so we must also replace the viewport slot to avoid rendering ScrollArea.Viewport
// without a ScrollArea.Root context (which throws during SSR).
const ChatConversationListViewportStyled = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'Viewport',
  overridesResolver: (_, styles) => styles.viewport
})(() => ({
  flex: 1,
  overflow: 'auto',
  overscrollBehavior: 'contain',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
}));

// No-op scrollbar: native overflow on the viewport above handles scrolling,
// so we don't need the Base UI ScrollArea.Scrollbar / Thumb components.
const NoopScrollbar = /*#__PURE__*/React.forwardRef(function NoopScrollbar() {
  return null;
});

// Props forwarded by ConversationListRenderedItem as additionalProps to every item slot.
// They are intentionally NOT forwarded to the DOM to avoid React unknown-prop warnings.
// NOTE: providing a custom shouldForwardProp REPLACES MUI's default exclusion list, so we
// must also explicitly exclude 'ownerState', 'theme', 'sx', and 'as' here.
if (process.env.NODE_ENV !== "production") NoopScrollbar.displayName = "NoopScrollbar";
const itemSlotShouldForwardProp = prop => !['conversation', 'selected', 'unread', 'focused', 'variant', 'ownerState', 'theme', 'sx', 'as'].includes(prop);
const ChatConversationListItemStyled = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'Item',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.item
})(({
  theme,
  ownerState
}) => _extends({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
  outline: 'none',
  borderRadius: 0,
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest
  }),
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none'
  },
  backgroundColor: ownerState?.selected ? (theme.vars || theme).palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: ownerState?.selected ? (theme.vars || theme).palette.action.selected : (theme.vars || theme).palette.action.hover
  },
  '&:focus-visible': {
    outline: `2px solid ${(theme.vars || theme).palette.primary.main}`,
    outlineOffset: -2
  }
}, ownerState?.variant === 'compact' && {
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1.5),
  borderRadius: theme.shape.borderRadius,
  '&:hover .MuiChatConversationList-itemActions': {
    opacity: 1
  }
}));

// ---------------------------------------------------------------------------
// Styled inner roots — pure styling, no rendering logic.
// These are used as the `root` slot inside the unstyled sub-components below.
// ---------------------------------------------------------------------------

const ChatConversationListItemAvatarRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemAvatar',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemAvatar
})(({
  theme
}) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: (theme.vars || theme).palette.grey[300],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  color: (theme.vars || theme).palette.text.secondary,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}));
const ChatConversationListItemContentRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemContent',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemContent
})(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1
}));
const ChatConversationListTitleRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemTitle',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemTitle
})(({
  theme,
  ownerState
}) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: ownerState?.unread ? theme.typography.fontWeightBold : theme.typography.fontWeightMedium,
  color: (theme.vars || theme).palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.4
}));
const ChatConversationListPreviewRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemPreview',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemPreview
})(({
  theme
}) => ({
  fontSize: theme.typography.caption.fontSize,
  color: (theme.vars || theme).palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.4
}));
const ChatConversationListTimestampRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemTimestamp',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemTimestamp
})(({
  theme
}) => ({
  fontSize: theme.typography.caption.fontSize,
  color: (theme.vars || theme).palette.text.disabled,
  flexShrink: 0,
  alignSelf: 'flex-start',
  paddingTop: '2px',
  whiteSpace: 'nowrap'
}));
const ChatConversationListUnreadBadgeRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemUnreadBadge',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemUnreadBadge
})(({
  theme,
  ownerState
}) => _extends({
  minWidth: 18,
  height: 18,
  borderRadius: 9,
  backgroundColor: (theme.vars || theme).palette.primary.main,
  color: (theme.vars || theme).palette.primary.contrastText,
  fontSize: '0.65rem',
  fontWeight: theme.typography.fontWeightBold,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 0.5),
  flexShrink: 0,
  alignSelf: 'center',
  lineHeight: 1
}, ownerState?.variant === 'compact' && {
  minWidth: 8,
  width: 8,
  height: 8,
  borderRadius: '50%',
  padding: 0,
  fontSize: 0,
  overflow: 'hidden'
}));

// ---------------------------------------------------------------------------
// Item actions — visible on hover in compact mode, always available in default.
// ---------------------------------------------------------------------------

const ChatConversationListItemActionsRoot = styled('div', {
  name: 'MuiChatConversationList',
  slot: 'ItemActions',
  shouldForwardProp: itemSlotShouldForwardProp,
  overridesResolver: (_, styles) => styles.itemActions
})(({
  theme,
  ownerState
}) => _extends({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0
}, ownerState?.variant === 'compact' && {
  opacity: 0,
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.shortest
  }),
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none'
  }
}));

// ---------------------------------------------------------------------------
// Wrapper slot components — keep the unstyled rendering logic, inject the
// styled root so MUI theming is applied.
// ---------------------------------------------------------------------------

const ChatConversationListItemAvatarStyled = /*#__PURE__*/React.forwardRef(function ChatConversationListItemAvatarStyled(props, ref) {
  return /*#__PURE__*/_jsx(ConversationListItemAvatar, _extends({
    ref: ref
  }, props, {
    slots: _extends({
      root: ChatConversationListItemAvatarRoot
    }, props.slots)
  }));
});
if (process.env.NODE_ENV !== "production") ChatConversationListItemAvatarStyled.displayName = "ChatConversationListItemAvatarStyled";
const ChatConversationListItemContentStyled = /*#__PURE__*/React.forwardRef(function ChatConversationListItemContentStyled(props, ref) {
  return /*#__PURE__*/_jsx(ConversationListItemContent, _extends({
    ref: ref
  }, props, {
    slots: _extends({
      root: ChatConversationListItemContentRoot
    }, props.slots)
  }));
});
if (process.env.NODE_ENV !== "production") ChatConversationListItemContentStyled.displayName = "ChatConversationListItemContentStyled";
const ChatConversationListTitleStyled = /*#__PURE__*/React.forwardRef(function ChatConversationListTitleStyled(props, ref) {
  return /*#__PURE__*/_jsx(ConversationListTitle, _extends({
    ref: ref
  }, props, {
    slots: _extends({
      root: ChatConversationListTitleRoot
    }, props.slots)
  }));
});
if (process.env.NODE_ENV !== "production") ChatConversationListTitleStyled.displayName = "ChatConversationListTitleStyled";
const ChatConversationListPreviewStyled = /*#__PURE__*/React.forwardRef(function ChatConversationListPreviewStyled(props, ref) {
  return /*#__PURE__*/_jsx(ConversationListPreview, _extends({
    ref: ref
  }, props, {
    slots: _extends({
      root: ChatConversationListPreviewRoot
    }, props.slots)
  }));
});
if (process.env.NODE_ENV !== "production") ChatConversationListPreviewStyled.displayName = "ChatConversationListPreviewStyled";
const ChatConversationListTimestampStyled = /*#__PURE__*/React.forwardRef(function ChatConversationListTimestampStyled(props, ref) {
  return /*#__PURE__*/_jsx(ConversationListTimestamp, _extends({
    ref: ref
  }, props, {
    slots: _extends({
      root: ChatConversationListTimestampRoot
    }, props.slots)
  }));
});
if (process.env.NODE_ENV !== "production") ChatConversationListTimestampStyled.displayName = "ChatConversationListTimestampStyled";
const ChatConversationListUnreadBadgeStyled = /*#__PURE__*/React.forwardRef(function ChatConversationListUnreadBadgeStyled(props, ref) {
  return /*#__PURE__*/_jsx(ConversationListUnreadBadge, _extends({
    ref: ref
  }, props, {
    slots: _extends({
      root: ChatConversationListUnreadBadgeRoot
    }, props.slots)
  }));
});

// Default inline SVG for the 3-dot "more" icon (MoreHoriz style).
if (process.env.NODE_ENV !== "production") ChatConversationListUnreadBadgeStyled.displayName = "ChatConversationListUnreadBadgeStyled";
function DefaultMoreIcon() {
  return /*#__PURE__*/_jsxs("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 24 24",
    fill: "currentColor",
    children: [/*#__PURE__*/_jsx("circle", {
      cx: "6",
      cy: "12",
      r: "2"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "12",
      cy: "12",
      r: "2"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "18",
      cy: "12",
      r: "2"
    })]
  });
}
const ChatConversationListItemActionsStyled = /*#__PURE__*/React.forwardRef(function ChatConversationListItemActionsStyled(props, ref) {
  return /*#__PURE__*/_jsx(ConversationListItemActions, _extends({
    ref: ref
  }, props, {
    slots: _extends({
      root: ChatConversationListItemActionsRoot
    }, props.slots),
    children: props.children ?? /*#__PURE__*/_jsx(DefaultMoreIcon, {})
  }));
});
if (process.env.NODE_ENV !== "production") ChatConversationListItemActionsStyled.displayName = "ChatConversationListItemActionsStyled";
const ChatConversationList = /*#__PURE__*/React.forwardRef(function ChatConversationList(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatConversationList'
  });
  const {
      variant = 'default',
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatConversationListUtilityClasses(classesProp);
  const isCompact = variant === 'compact';
  return /*#__PURE__*/_jsx(ConversationListRoot, _extends({
    ref: ref,
    variant: variant
  }, other, {
    slots: _extends({
      root: slots?.root ?? ChatConversationListStyled,
      scroller: slots?.scroller ?? ChatConversationListScrollerStyled,
      viewport: slots?.viewport ?? ChatConversationListViewportStyled,
      scrollbar: slots?.scrollbar ?? NoopScrollbar,
      scrollbarThumb: slots?.scrollbarThumb ?? NoopScrollbar,
      item: slots?.item ?? ChatConversationListItemStyled,
      itemAvatar: slots?.itemAvatar ?? ChatConversationListItemAvatarStyled,
      itemContent: slots?.itemContent ?? ChatConversationListItemContentStyled,
      title: slots?.title ?? ChatConversationListTitleStyled,
      preview: slots?.preview ?? ChatConversationListPreviewStyled,
      timestamp: slots?.timestamp ?? ChatConversationListTimestampStyled,
      unreadBadge: slots?.unreadBadge ?? ChatConversationListUnreadBadgeStyled,
      itemActions: slots?.itemActions ?? ChatConversationListItemActionsStyled
    }, slots),
    slotProps: _extends({}, slotProps, {
      root: _extends({
        className: clsx(classes.root, className, isCompact && classes.compact),
        sx
      }, slotProps?.root),
      scroller: _extends({
        className: classes.scroller
      }, slotProps?.scroller),
      item: ownerState => _extends({
        className: clsx(classes.item, ownerState?.selected && classes.itemSelected, ownerState?.unread && classes.itemUnread, ownerState?.focused && classes.itemFocused)
      }, typeof slotProps?.item === 'function' ? slotProps.item(ownerState) : slotProps?.item),
      itemAvatar: _extends({
        className: classes.itemAvatar
      }, slotProps?.itemAvatar),
      itemContent: _extends({
        className: classes.itemContent
      }, slotProps?.itemContent),
      title: _extends({
        className: classes.itemTitle
      }, slotProps?.title),
      preview: _extends({
        className: classes.itemPreview
      }, slotProps?.preview),
      timestamp: _extends({
        className: classes.itemTimestamp
      }, slotProps?.timestamp),
      unreadBadge: _extends({
        className: classes.itemUnreadBadge
      }, slotProps?.unreadBadge),
      itemActions: _extends({
        className: classes.itemActions
      }, slotProps?.itemActions)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatConversationList.displayName = "ChatConversationList";
process.env.NODE_ENV !== "production" ? ChatConversationList.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  classes: PropTypes.object,
  className: PropTypes.string,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * The visual variant of the conversation list.
   * - `'default'` – shows avatar, title, preview, timestamp, and unread badge.
   * - `'compact'` – shows only a small unread indicator, the title, and an actions button.
   * @default 'default'
   */
  variant: PropTypes.oneOf(['compact', 'default'])
} : void 0;
export { ChatConversationList };